import { Dialog, Input } from '@cimplico/components';
import { useState } from 'react';
import { toastNotification } from '../../components/toast/Toast';
import { useUpdateProcedureCategories } from '../../queries/use-update-categories';
import { parseTitle } from '../../utilities/formatting';

type EditProcedureCategoryConfirmProps = {
    templateId: string;
    categoryId?: string;
    defaultCategoryName?: string;
    isOpen: boolean;
    onClose: () => void;
};

function EditProcedureCategoryConfirm({
    templateId,
    categoryId,
    defaultCategoryName,
    isOpen,
    onClose,
}: EditProcedureCategoryConfirmProps): JSX.Element {
    const [name, setName] = useState<{ value?: string; errorMessage?: string }>({ value: defaultCategoryName });

    const updateProcedureCategoryQuery = useUpdateProcedureCategories(templateId);

    function handleClose(): void {
        onClose();
        setName({ value: defaultCategoryName, errorMessage: undefined });
    }

    function handleSubmit(): void {
        if (!categoryId) {
            return;
        }

        if (!name?.value) {
            setName((prev) => ({ ...prev, errorMessage: 'A category name must be provided.' }));
            return;
        }

        if (name.value === defaultCategoryName) {
            handleClose();
            return;
        }

        updateProcedureCategoryQuery.mutate(
            [
                {
                    id: categoryId,
                    category: { name: name.value, reference: parseTitle(name.value, 3) },
                },
            ],
            {
                onSuccess: () => {
                    onClose();
                    toastNotification('success', 'Procedure category name updated!');
                },
                onError: (e) => {
                    const result = e.response?.data.results[0];
                    setName((prev) => ({ ...prev, errorMessage: result.error?.fields?.name }));
                    toastNotification('error', 'Unable to update procedure category name!');
                },
            }
        );
    }

    return (
        <Dialog
            type="info"
            title="Edit category name"
            description="Do you want to edit the name for this procedure category?"
            isOpen={isOpen}
            primaryAction="Save"
            secondaryAction="Cancel"
            onClose={handleClose}
            onSecondaryActionClick={handleClose}
            onPrimaryActionClick={handleSubmit}
            isLoading={updateProcedureCategoryQuery.isLoading}
        >
            <div className="pr-6 pb-4">
                <Input
                    type="text"
                    label="Name"
                    onChange={(e): void => setName({ value: e.target.value, errorMessage: undefined })}
                    errorMessage={name?.errorMessage}
                    defaultValue={defaultCategoryName}
                    autoFocus
                />
            </div>
        </Dialog>
    );
}

export default EditProcedureCategoryConfirm;
